// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
export interface SvResponse {
  svs: { [key: string]: Sv };
  version: string;
  hce_version: string;
}

export interface Sv {
  chr_id: number;
  chr_to_id?: number;
  contig_id: number;
  genome_region_names: string[];
  is_expanded?: boolean;
  inverted?: boolean;
  length: number;
  meta: Meta;
  quality_score: number;
  reason?: Reason;
  region: Region;
  region_frags_info: RegionFragsInfo;
  segment_to?: SegmentTo;
  type: Type;
  zygosity: Zygosity;
  hce_sv_id: string;
  Embedded: Embedded;
  lytx?: Lytx;
}

export enum Embedded {
  N = "N",
  Y = "Y",
}

export interface Lytx {
  percent_presence_lrs_cohort?: number;
  percent_presence_gnomad_cohort?: number;
  percent_presence_ont_cohort?: number;
  masked?: boolean;
  mol_check?: boolean;
  cluster_id?: number;
  rep_sv_in_cluster?: boolean;
}

export interface Meta {
  is_detectable: boolean;
  merged_from_ids: any[];
}

export enum Reason {
  Length = "Length",
  Probe = "Probe",
  ProbeAndLength = "ProbeAndLength",
}

export interface Region {
  chr: Chr;
  contig: Contig;
  is_fuzzy: boolean;
  is_one_to_one: boolean;
  is_one_to_one_on_contig: boolean;
  is_tail_region: boolean;
  next_al_id: number;
  prev_al_id: number;
}

export interface Chr {
  end: End;
  end_index: number;
  is_clear: boolean;
  is_degenerated: boolean;
  is_fuzzy: boolean;
  is_pure_overlapped: boolean;
  length: number;
  start: Start;
  start_index: number;
  affected_regions?: string[];
  affected_genes?: string[];
  affected_genes_hrefs?: string[];
}

export interface Contig {
  end: End;
  end_index: number;
  is_clear: boolean;
  is_degenerated: boolean;
  is_fuzzy: boolean;
  is_pure_overlapped: boolean;
  length: number;
  start: Start;
  start_index: number;
  affected_regions?: string[];
  affected_genes?: string[];
  affected_genes_hrefs?: string[];
}

export interface SegmentTo {
  end: End;
  end_index: number;
  is_clear: boolean;
  is_degenerated: boolean;
  is_fuzzy: boolean;
  is_pure_overlapped: boolean;
  length: number;
  start: Start;
  start_index: number;
  affected_regions?: string[];
  affected_genes?: string[];
  affected_genes_hrefs?: string[];
}

export interface Start {
  index: number;
  max: number;
  min: number;
  pos_ind: PosInd;
  pos_kb: PosKb;
}

export interface End {
  index: number;
  max: number;
  min: number;
  pos_ind: PosInd;
  pos_kb: PosKb;
}

export interface PosInd {
  max: number;
  min: number;
}

export interface PosKb {
  max: number;
  min: number;
}

export interface RegionFragsInfo {
  contig_frags: number[];
  contig_frags_stats: { [key: string]: ContigFragsStat[] };
  ref_frags: number[];
  region_aggregated_stats: RegionAggregatedStats;
}

export interface ContigFragsStat {
  coverage: number;
  depth: number;
  mean: number;
  sd: number;
}

export interface RegionAggregatedStats {
  coverage: Coverage;
  depth: Depth;
  sd: Sd;
}

export interface Coverage {
  max: number | null;
  mean: number | null;
  median: number | null;
  min: number | null;
}

export interface Depth {
  max: number | null;
  mean: number | null;
  median: number | null;
  min: number | null;
}

export interface Sd {
  max: number | null;
  mean: number | null;
  median: number | null;
  min: number | null;
}

export enum Type {
  Deletion = "Deletion",
  Duplication = "Duplication",
  Insertion = "Insertion",
  Inversion = "Inversion",
  Translocation = "Translocation",
}

export enum Zygosity {
  Heterozygous = "Heterozygous",
  Homozygous = "Homozygous",
  Unknown = "Unknown",
}

export interface GeneLink {
  name: string;
  id: string;
}

export interface SvListTableData {
  svsKey: number;                     // svs
  svId: string;                       // svs.hce_sv_id
  chrId: number;                      // svs.chr_id
  start: number | null;               // svs.region.chr.start.min
  end: number | null;                 // svs.region.chr.end.max
  length: number | null;              // svs.length
  chrToId: number | null;             // svs.chr_to_id
  startTo: number | null;             // svs.segment_to.start.min
  endTo: number | null;               // svs.segment_to.end.max
  lengthTo: number | null;            // svs.segment_to.length
  type: Type;                         // svs.type
  contig: string | null;              // svs.contig_id
  zygosity: Zygosity;                 // svs.zygosity
  flaggedSv: Embedded;                // svs.Embedded
  genename: string[];                 // svs.region.chr.affected_genes
  genenameTo: string[];               // svs.segment_to.affected_genes
  geneLinks: GeneLink[];
  geneLinksTo: GeneLink[];
  minimumIntervalDepth: number | null;// svs.region_frags_info.region_aggregated_stats.coverage.min
  medianIntervalDepth: number | null; // svs.region_frags_info.region_aggregated_stats.coverage.median
  qualityScore: number | null;        // svs.quality_score
  reason: Reason | null;              // svs.reason
  molCheck: boolean | null;           // svs.lytx.mol_check
  colorSdb: number | null;            // svs.lytx.percent_presence_lrs_cohort
  gnomAd: number | null;              // svs.lytx.percent_presence_gnomad_cohort
  ontLrs: number | null;              // svs.lytx.percent_presence_ont_cohort
  masked: boolean | null;             // svs.lytx.masked
  bestSvPerCluster: boolean | null;   // svs.lytx.rep_sv_in_cluster
}
